import React, { Suspense, useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import { Box, Loader, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ActionType, HeaderProps, ModalVariant } from '@/components/Modals/types';
import { EntityTagInput, EntityTagsInput, TagAvailableOnEnum } from '@/types/schema';
import dynamic from '@/utils/dynamic';

import LocalisedSttingInfo from '../../../course/views/CourseBuilder/components/SidebarDetails/Settings/components/Dialogs/components/Certificate/localizedSettingInfo';
import { adaptTagsOnSubmit } from '../../utils';
import useAttachTags from './hooks/useAttachTags';

// fix the entity filter @iamsaumya
const AttachTags = dynamic(
  () =>
    import(
      /* webpackChunkName: "attach-tags" */
      '@/modules/corporate/modules/tags/components/AttachTags'
    ),
);

export interface AttachModalProps {
  tags: EntityTagInput[];
  onSubmit: (tags: EntityTagsInput | null | undefined) => Promise<boolean | undefined>;
  onClose: () => void;
  availableOn: TagAvailableOnEnum[];
  subWorkspaceIds?: string[];
  contentName: string;
  action?: Pick<ActionType, 'text'>;
  header?: Partial<Pick<HeaderProps, 'title' | 'description'>>;
  isSharedItem?: boolean;
  hasError?: boolean;
  fetchTagWithProviders?: boolean;
  readonly?: boolean;
}

export const REQUIRED_TAGS_EMPTY_ERROR = 'Tags:Form.Error.TagsValuesEmpty';

/*
  dont use useEffect to set tags.. this may lead to errors..
  since tags are resolved in user query, any change in user will lead to invalidate of tags
**/

function AttachTagsModal(props: AttachModalProps) {
  const {
    tags,
    onClose,
    onSubmit,
    availableOn,
    contentName,
    action,
    header,
    isSharedItem,
    hasError,
    subWorkspaceIds,
    readonly,
  } = props;

  const { t } = useTranslation('Tags');
  const [isSubmitting, setSubmitting] = useState(false);

  const { tag, onTagChange, onTagValueChange, isFetching, validate, error, onRemoveTag } =
    useAttachTags({
      tags,
      availableOn,
      hasError,
      subWorkspaceIds,
    });

  const onModalSubmit = useCallback(async () => {
    if (!validate(tag)) {
      return;
    }
    setSubmitting(true);
    const done = await onSubmit(adaptTagsOnSubmit(tag));
    if (done) {
      onClose();
    }
    setSubmitting(false);
  }, [onClose, onSubmit, tag, validate]);

  const NoTagsAddedYet = (
    <EmptyState
      className="w-full flex-1"
      description={t('Tags:Modal.EmptyStateDescription')}
      title={t('Tags:Modal.EmptyStateTitle')}
    />
  );

  return (
    <Modal
      action={
        readonly
          ? undefined
          : {
              text: t('Common:Actions.Save'),
              onSubmit: onModalSubmit,
              discardText: t('Common:Cancel'),
              isSubmitting,
              ...action,
            }
      }
      footer={readonly ? <LocalisedSttingInfo /> : undefined}
      header={{
        title: t('Common:Form.Tags'),
        description: t('Tags:Modal.TagDescription', { contentName }),
        ...header,
      }}
      variant={ModalVariant.FullHeight}
      onClose={onClose}>
      <Box className="flex flex-1 flex-col px-28 pb-128">
        {isFetching ? (
          <Stack alignItems="center" className="h-full w-full flex-1" justifyContent="center">
            <Loader />
          </Stack>
        ) : (
          <Suspense fallback={<Loader />}>
            <>
              {error ? (
                <Typography className="mb-32" color="text-text-error" variant="body-short-03">
                  {error}
                </Typography>
              ) : null}
              <Box className="flex flex-1 flex-col flex-wrap">
                <AttachTags
                  EmptyState={NoTagsAddedYet}
                  availableOn={availableOn}
                  error={error}
                  isSharedItem={isSharedItem}
                  readonly={readonly}
                  selectedTagValues={tag?.tagValues}
                  subWorkspaceIds={subWorkspaceIds}
                  tags={tag?.tags}
                  onRemoveTag={onRemoveTag}
                  onTagChange={onTagChange}
                  onTagsValueChange={onTagValueChange}
                />
              </Box>
            </>
          </Suspense>
        )}
      </Box>
    </Modal>
  );
}

export default React.memo(AttachTagsModal);
